/**
 * Draft local pentru notițe — scris în localStorage la fiecare keystroke
 * (debounce 500ms). Evită trimiterea la server la fiecare typo (fiecare PATCH
 * generează versiune arhivată — prea zgomotos).
 *
 * Draftul conține HTML-ul decriptat al notiței, deci e CRIPTAT înainte de a
 * atinge discul: `encryptSym` (secretbox) cu o cheie derivată din `master_key`
 * (context `draft-v1`). Fără sesiune activă nu poate fi citit de nimeni, așa
 * că supraviețuiește logout-ului și auto-logout-ului fără să expună conținut.
 *
 * Server save se întâmplă doar la:
 * - unmount editor (user schimbă notiță / navighează la altă pagină)
 * - click explicit pe „Salvează"
 *
 * După server save cu succes, draft-ul local se șterge.
 */

import {
  base64ToBytes,
  bytesToBase64,
  bytesToStr,
  decryptSym,
  deriveSubkey,
  encryptSym,
  strToBytes,
} from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { useDraftsStore } from '@/stores/drafts';
import { registerSessionCleanup } from '@/stores/sessionCleanup';

const KEY_PREFIX = 'cerbes-draft';
const SUBKEY_CONTEXT = 'cerbes-draft-v1';

interface Draft {
  title: string;
  html: string;
  savedAt: number;
}

/** Forma de pe disc: doar ciphertext. */
interface StoredDraft {
  v: 2;
  nonce: string;
  ciphertext: string;
}

function keyFor(userId: string, noteId: string): string {
  return `${KEY_PREFIX}-${userId}-${noteId}`;
}

async function draftKey(): Promise<Uint8Array | null> {
  const masterKey = useAuthStore.getState().masterKey;
  return masterKey ? deriveSubkey(masterKey, SUBKEY_CONTEXT) : null;
}

function isDraft(value: unknown): value is Draft {
  const d = value as Draft | null;
  return (
    typeof d === 'object' &&
    d !== null &&
    typeof d.title === 'string' &&
    typeof d.html === 'string' &&
    typeof d.savedAt === 'number'
  );
}

/** Scanează localStorage la mount și populează store-ul cu note IDs care
 *  au draft asociat user-ului curent. */
export function indexExistingDrafts(userId: string): void {
  if (typeof localStorage === 'undefined') return;
  const prefix = `${KEY_PREFIX}-${userId}-`;
  const ids: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) {
      ids.push(k.slice(prefix.length));
    }
  }
  useDraftsStore.getState().setAll(ids);
}

export async function readDraft(userId: string, noteId: string): Promise<Draft | null> {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(keyFor(userId, noteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft | Draft;

    if ('v' in parsed && parsed.v === 2) {
      const key = await draftKey();
      if (!key) return null;
      const plain = await decryptSym(
        { ciphertext: base64ToBytes(parsed.ciphertext), nonce: base64ToBytes(parsed.nonce) },
        key,
      );
      const draft: unknown = JSON.parse(bytesToStr(plain));
      return isDraft(draft) ? draft : null;
    }

    // Format vechi (plaintext pe disc): îl re-scriem criptat imediat, ca să
    // nu pierdem munca nesalvată și să nu mai rămână în clar.
    if (isDraft(parsed)) {
      await storeDraft(userId, noteId, parsed);
      return parsed;
    }
    return null;
  } catch {
    // Nedecriptabil (ex. parola master schimbată între timp) sau corupt.
    clearDraft(userId, noteId);
    return null;
  }
}

async function storeDraft(userId: string, noteId: string, draft: Draft): Promise<void> {
  const key = await draftKey();
  if (!key) {
    // Fără cheie nu scriem nimic; un eventual draft vechi în clar e eliminat.
    localStorage.removeItem(keyFor(userId, noteId));
    return;
  }
  const sealed = await encryptSym(strToBytes(JSON.stringify(draft)), key);
  const stored: StoredDraft = {
    v: 2,
    nonce: bytesToBase64(sealed.nonce),
    ciphertext: bytesToBase64(sealed.ciphertext),
  };
  localStorage.setItem(keyFor(userId, noteId), JSON.stringify(stored));
}

export async function writeDraft(
  userId: string,
  noteId: string,
  draft: Omit<Draft, 'savedAt'>,
): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  try {
    await storeDraft(userId, noteId, { ...draft, savedAt: Date.now() });
    useDraftsStore.getState().markDraft(noteId);
  } catch {
    // quota depășită; ignorăm — user are oricum state-ul în memorie
  }
}

export function clearDraft(userId: string, noteId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(keyFor(userId, noteId));
  useDraftsStore.getState().clearDraft(noteId);
}

/**
 * Elimină drafturile rămase în formatul vechi, necriptat. Apelat la logout:
 * cele criptate (v2) pot rămâne pe disc, cele în clar nu.
 */
export function purgePlaintextDrafts(): void {
  if (typeof localStorage === 'undefined') return;
  const stale: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(KEY_PREFIX + '-')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(k) ?? 'null') as { v?: number } | null;
      if (parsed?.v !== 2) stale.push(k);
    } catch {
      stale.push(k);
    }
  }
  for (const k of stale) localStorage.removeItem(k);
}

registerSessionCleanup(() => {
  purgePlaintextDrafts();
  useDraftsStore.getState().resetAll();
});
